/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { AppValues } from '@/Constants/AppValues'
import { Server } from '@/Constants/Server'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinComponent from '@/Mixins/MixinComponent'
import MixinFetch from '@/Mixins/MixinFetch'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @internal Const @name NotificationsButton                                                    **/
/**************************************************************************************************/
const NotificationsButton = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'NotificationsButton', mixins: [MixinComponent, MixinFetch, MixinResponsive],

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			states:
			{
				defaultIdSystem: AppValues.ObjectIds.Modules.SERVICES,
				notifications: [] as Array<any>,
				showNotifications: false
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	async created() : Promise<void>
	{
		this.FetchNotificationsForIdSystem(this.states.defaultIdSystem)
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		GetNotifications() : Array<any>
		{
			return (Store.getters.getStoredTempNotifications as Array<any>)
			.filter((x) => !x.closed)
			.sort((a, b) => a.dateCreation < b.dateCreation ? 1 : -1)
		},

		GetNumUnreadNotifications() : number
		{
			return this.GetNotifications.filter((x: any) => !x.read).length
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		async FetchNotificationsForIdSystem(idSystem: string) : Promise<void>
		{
			if (Store.getters.getStoredUpdateAllInProgress) return
			PrintHelper.warning('[NotificationsButton]:fetchNotificationsForIdSystem() Method Triggered!')
			const response = await Store.dispatch('fetchNotificationsByIdSystem', { _idSystem: idSystem })
			this.Set<NotificationsButtonRef['states']>({ notifications: response })
		},

		GetNotificationDate(notification: any) : string
		{
			const parsedDate = TypesHelper.toDatePartsFromString(notification.dateCreation)!
			return `${ parsedDate.date } ${ parsedDate.time }`
		},

		GetNotificationItemReadClass(notification: any) : string
		{
			return notification.read ? 'read' : ''
		},

		async OnNBMarkAllAsClosed(closed: boolean) : Promise<void>
		{
			PrintHelper.warning('[NotificationsButton]:onNBMarkAllAsClosed event triggered')
			this.states.notifications.forEach((x) => x.closed = closed)
			Store.commit('storeUpdateAllInProgress', true)
			for (const x of this.states.notifications) await this.OnNBMarkNotificationAsClosed(x, closed, false)
			Store.commit('storeUpdateAllInProgress', false)
		},

		async OnNBMarkAllAsRead(read: boolean) : Promise<void>
		{
			PrintHelper.warning('[NotificationsButton]:onNBMarkAllAsRead event triggered')
			this.states.notifications.forEach((x) => x.read = read)
			Store.commit('storeUpdateAllInProgress', true)
			for (const x of this.states.notifications) await this.OnNBMarkNotificationAsRead(x, read, false)
			Store.commit('storeUpdateAllInProgress', false)
		},

		async OnNBMarkNotificationAsClosed(notification: any, closed = true, autoFetch = true) : Promise<void>
		{
			PrintHelper.warning('[NotificationsButton]:onNBMarkNotificationAsClosed event triggered')
			notification.closed = closed

			// Objeto con las Propiedades requeridas por la Petición.
			const body =
			{
				_idNotification: notification._idNotification,
				closed: notification.closed,
				message: notification.message
			}

			// Realizar la Petición al servidor.
			const response = await this.Fetch(Server.Fetching.Method.PATCH, Server.Routes.Notifications.UpdateNotificationById, body)

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response?.status === Server.Response.StatusCodes.SUCCESS)
			{
				if (autoFetch) this.FetchNotificationsForIdSystem(this.states.defaultIdSystem)
			}
		},

		async OnNBMarkNotificationAsRead(notification: any, read = true, autoFetch = true) : Promise<void>
		{
			PrintHelper.warning('[NotificationsButton]:onNBMarkNotificationAsRead event triggered')
			notification.read = read

			// Objeto con las Propiedades requeridas por la Petición.
			const body =
			{
				_idNotification: notification._idNotification,
				read: notification.read
			}

			// Realizar la Petición al servidor.
			const response = await this.Fetch(Server.Fetching.Method.PATCH, Server.Routes.Notifications.UpdateNotificationReadById, body)

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response?.status === Server.Response.StatusCodes.SUCCESS)
			{
				if (autoFetch) this.FetchNotificationsForIdSystem(this.states.defaultIdSystem)
			}
		},

		OnNBToggleWindow() : void
		{
			PrintHelper.warning('[NotificationsButton]:onNBToggleWindow event triggered')
			this.Set<NotificationsButtonRef['states']>({ showNotifications: !this.states.showNotifications })
		}
	},

	/*************************************************/
	/** @Watch                                      **/
	/*************************************************/
	watch:
	{
		['$store.state.G_Notifications.updateAllInProgress'](newValue: boolean) : void
		{
			if (newValue === false) this.FetchNotificationsForIdSystem(this.states.defaultIdSystem)
		}
	}
})

/**************************************************************************************************/
/** @export Component @name NotificationsButton                                                  **/
/**************************************************************************************************/
export default NotificationsButton

/**************************************************************************************************/
/** @export Type @name NotificationsButtonRef                                                    **/
/**************************************************************************************************/
export type NotificationsButtonRef = InstanceType<typeof NotificationsButton>